import type { JSX } from "preact";
import { clearSearch, logout, runSearch, selectMailbox } from "../actions";
import {
  fullMessage,
  mobilePane,
  searchLoading,
  searchQuery,
  showSettings,
  showShortcuts,
  userEmail,
} from "../state";
import { Icon } from "./icons";
import { Logo } from "./Logo";

let searchTimer: ReturnType<typeof setTimeout>;

export function Topbar(): JSX.Element {
  const email = userEmail.value;
  const initial = email ? email[0]!.toUpperCase() : "?";
  const q = searchQuery.value;
  const loading = searchLoading.value;
  const pane = mobilePane.value;

  function handleSearchInput(e: Event): void {
    const value = (e.currentTarget as HTMLInputElement).value;
    clearTimeout(searchTimer);
    searchTimer = setTimeout(() => {
      void runSearch(value);
    }, 300);
  }

  // Mobile title reflects current pane
  let mobileTitle = "MailTrack";
  if (pane === "reader") {
    mobileTitle = fullMessage.value?.subject ?? "Message";
  } else if (pane === "list") {
    // will be set below based on context; fallback is "MailTrack"
    mobileTitle = "Mail";
  }

  function goBack(): void {
    if (pane === "reader") {
      mobilePane.value = "list";
    } else if (pane === "list") {
      mobilePane.value = "sidebar";
    }
  }

  return (
    <header class="flex h-14 shrink-0 items-center border-b border-navbar bg-ink-1">
      {/* Mobile back button — only visible on small screens, hidden when on sidebar pane */}
      {pane !== "sidebar" && (
        <button
          class="icon-btn ml-2 md:hidden"
          title="Back"
          aria-label="Go back"
          onClick={goBack}
        >
          <Icon name="chevronLeft" size={20} />
        </button>
      )}

      {/* Logo area — always visible on md+, hidden on mobile */}
      <button
        class="hidden w-64 items-center gap-2 px-4 md:flex hover:opacity-80 transition-opacity"
        title="Go to Inbox"
        onClick={() => void selectMailbox("INBOX")}
      >
        <Logo size={28} />
        <span class="text-sm font-semibold">MailTrack</span>
      </button>

      {/* Mobile title — only on small screens */}
      <span class="flex-1 truncate px-2 text-sm font-semibold text-fg md:hidden">
        {mobileTitle}
      </span>

      <div class="hidden flex-1 items-center gap-2 px-4 md:flex">
        <div class="relative w-full max-w-xl">
          <span class="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-muted">
            {loading ? (
              <span class="block h-4 w-4 animate-spin rounded-full border-2 border-muted border-t-accent" />
            ) : (
              <Icon name="search" size={18} />
            )}
          </span>
          <input
            class="field pl-9 pr-8"
            placeholder="Search…"
            value={q}
            onInput={handleSearchInput}
          />
          {q && (
            <button
              class="absolute right-2 top-1/2 -translate-y-1/2 text-muted hover:text-fg"
              title="Clear search"
              onClick={() => clearSearch()}
              aria-label="Clear search"
            >
              <Icon name="x" size={16} />
            </button>
          )}
        </div>

        <div class="ml-auto flex items-center gap-1">
          <button
            class="icon-btn"
            title="Keyboard shortcuts"
            aria-label="Show keyboard shortcuts"
            onClick={() => { showShortcuts.value = !showShortcuts.value; }}
          >
            <span class="text-sm font-medium text-muted">?</span>
          </button>
          <button
            class="icon-btn"
            title="Settings"
            onClick={() => { showSettings.value = !showSettings.value; }}
          >
            <Icon name="settings" size={18} />
          </button>
          <button
            onClick={() => void logout()}
            class="flex items-center gap-2 rounded-lg px-2 py-1.5 transition-colors hover:bg-ink-3"
            title="Sign out"
          >
            <span class="flex h-7 w-7 items-center justify-center rounded-full bg-accent text-xs font-semibold text-fg-on-accent">
              {initial}
            </span>
            <span class="hidden max-w-[12rem] truncate text-xs text-fg-dim sm:block">{email}</span>
            <Icon name="logout" size={16} class="text-muted" />
          </button>
        </div>
      </div>

      {/* Mobile right actions */}
      <div class="flex items-center gap-1 px-2 md:hidden">
        <button
          onClick={() => void logout()}
          class="flex h-8 w-8 items-center justify-center rounded-full bg-accent text-xs font-semibold text-fg-on-accent"
          title="Sign out"
          aria-label="Sign out"
        >
          {initial}
        </button>
      </div>
    </header>
  );
}
